/**
 * Shared Arabic vocabulary for the PRE-BOOKING readiness panel.
 *
 * Renders `be/services/prebooking/readinessResolver.js` — is the unit free, is it
 * clean, when can the guest get in — for a thread with no booking behind it.
 * Spec: `tasks/pre-booking-context-panel.md` (increment 4).
 *
 * Centralised for the same reason `config/arrivalLabels.js` is, and that reason is
 * not tidiness: agreeing on the DATA while retyping the SENTENCES is drift that is
 * invisible in a diff, and the words are what an operator acts on at 04:00. The
 * parked overnight auto-reply (§6) would be the second consumer of exactly these
 * strings, and it must never invent its own phrasing for the same verdict.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE RULES ARE ENCODED IN THE WORDING ITSELF. Read them before editing a string.
 *
 * 🔴 D103 — NOTHING HERE MAY OFFER IMMEDIATE ENTRY. The door code's validity window
 *    derives from the property's check-in time, so the lock physically cannot admit
 *    a guest before it; an enquiry at 04:00 cannot get in until ~14:00 however the
 *    message is worded. Every entry string therefore STATES a time («الدخول من …»)
 *    and none of them invites arrival. A panel that contradicts the door is worse
 *    than no panel.
 *
 * 🔴 D101 — AN UNKNOWN IS NEVER DRESSED AS A SOFT YES. «تعذّر التحقق» and «غير معروفة»
 *    say we could not look; they never soften into "probably fine". Where an absence
 *    could be misread as a negative claim about the unit, the string says so out loud
 *    (see NO_TURNOVER_ON_RECORD), the same way `ARRIVAL_NO_EVIDENCE_HINT` does one
 *    panel over.
 *
 * 🔴 D102 — WE STATE OBSERVED STATE, WE NEVER PROMISE. «متاحة للحجز» is a reading of
 *    our calendar at a moment in time; «الشقة لك» would be a hold we cannot honour.
 *    NOT_A_HOLD_AR says this in the panel, beside the observed-at stamp.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Arabic-only, per the Arabic-first mandate.
 */

/**
 * The traffic light (D100) — tone ONLY, deliberately with no words of its own.
 *
 * The badge answers "can we offer this unit?", and the sentence beside it always
 * spells the answer out: COLOUR IS NEVER THE ONLY CHANNEL. Giving this map a label
 * too would put a second, shorter verdict on the same line as the real one, and the
 * two would start to disagree the first time a status was added.
 *
 * 🟢 free AND ready · 🟡 free but not ready or unclear · 🔴 not free, or we cannot
 * tell whether it is free.
 */
export const CONFIDENCE_TONE = {
  GREEN: 'is-go',
  AMBER: 'is-caution',
  RED: 'is-stop'
};

/** Unknown confidence is treated as stop, never as a missing badge. */
export const CONFIDENCE_TONE_FALLBACK = 'is-stop';

/**
 * Availability. Keys are the resolver's `availability.status`.
 *
 * «للحجز» is load-bearing, not padding: «متاحة» alone reads as "available to walk
 * into" beside a guest asking to come now. What we can prove is that the calendar is
 * free to BOOK — entry is a separate line with a separate time on it.
 */
export const AVAILABILITY_VIEW = {
  AVAILABLE: 'متاحة للحجز',
  UNAVAILABLE: 'غير متاحة للحجز',
  // NOT «قد تكون متاحة». We did not look and half-succeed; we failed to look.
  UNKNOWN: 'تعذّر التحقق من التوفر'
};

export const AVAILABILITY_VIEW_FALLBACK = 'تعذّر التحقق من التوفر';

/**
 * Why the calendar said what it said. Keys are the resolver's `availability.reason`,
 * which is either one of its own REASON constants or the availability engine's
 * `conflictType` passed through verbatim.
 */
export const AVAILABILITY_REASON_AR = {
  // The resolver's own — each one a "we could not look", never a "no".
  NO_CALENDAR_DATA: 'لا توجد بيانات تقويم لهذه الوحدة في هذه الفترة.',
  PARTIAL_CALENDAR_DATA: 'بيانات التقويم غير مكتملة لكل ليالي الفترة — بعض الليالي غير معروفة.',
  LOOKUP_FAILED: 'تعذّر الاطلاع على التقويم.',

  // The engine's conflict types — these ARE real answers about the calendar.
  existing_booking: 'يوجد حجز قائم في هذه الفترة.',
  // A relocated guest is physically inside this unit (tasks/guest-relocation-v1.md, L3) — refused
  // on the same terms as an existing booking, and the operator needs to be told which of the two
  // it is: one means "sold", this one means "someone is in there right now".
  guest_in_house: 'يوجد ضيف تم نقله إلى هذه الوحدة ويقيم بها في هذه الفترة.',
  no_inventory: 'لا توجد وحدة شاغرة في هذه الفترة.',
  stop_sell: 'البيع موقوف على هذه الفترة.',
  closed_to_arrival: 'الوصول مغلق في تاريخ الدخول المطلوب.',
  min_stay: 'الحد الأدنى للإقامة أطول من الفترة المطلوبة.',
  max_stay: 'الفترة المطلوبة تتجاوز الحد الأقصى للإقامة.'
};

export const AVAILABILITY_REASON_UNKNOWN = 'لا تتوفر تفاصيل إضافية عن حالة التقويم.';

/**
 * Cleaning. Keys are the resolver's `cleaning.status`.
 *
 * `standalone` follows the `arrivalLabels.js` convention: the wording to use where
 * nothing nearby says what the state is ABOUT. On the collapsed line the cleaning
 * word sits beside «متاحة للحجز», so a bare «غير معروفة» would read as "unknown
 * WHAT?"; inside the body it sits under its own «النظافة» heading and the short form
 * is right. Consumers read `standalone || label`.
 *
 * READY and NOT_APPLICABLE carry a standalone form they will rarely use — the
 * collapsed line only ever composes a cleaning word when the badge is amber — but a
 * map with holes in it is a map whose next reader has to know which keys are safe.
 */
export const CLEANING_VIEW = {
  READY: { label: 'جاهزة', standalone: 'الوحدة نظيفة' },
  NOT_READY: { label: 'تحتاج تنظيف', standalone: 'تحتاج تنظيف' },
  UNKNOWN: { label: 'غير معروفة', standalone: 'النظافة غير معروفة' },
  // Not a failure — the question does not apply yet. A stay three weeks out says
  // nothing about cleanliness today, because guests come and go in between.
  NOT_APPLICABLE: { label: 'لا تنطبق اليوم', standalone: 'حالة النظافة لا تنطبق اليوم' }
};

export const CLEANING_VIEW_FALLBACK = {
  label: 'غير معروفة',
  standalone: 'النظافة غير معروفة'
};

/** Why. Keys are the resolver's `cleaning.reason`. */
export const CLEANING_REASON_AR = {
  // ⚠️ THE ABSENCE IS QUALIFIED IN THE SAME BREATH, and this is the panel's D101
  // sentence. The ops dashboard resolves this same absence to "ready" on purpose —
  // right for an internal exception list, quoted to a paying guest it is a lie.
  NO_TURNOVER_ON_RECORD:
    'لا توجد مهمة تنظيف مسجّلة لهذه الوحدة. هذا لا يعني أنها غير نظيفة — يعني أننا لا نستطيع التأكيد.',
  TURNOVER_COMPLETED: 'اكتمل التنظيف قبل موعد الدخول.',
  TURNOVER_LATE: 'اكتمل التنظيف بعد موعد الدخول.',
  // "On track" is a promise about the future; the panel may only state the present.
  TURNOVER_OUTSTANDING: 'مهمة التنظيف لم تكتمل بعد.',
  NO_CHECKIN_TIME: 'تعذّر تحديد موعد الدخول لهذه الوحدة.',
  ARRIVAL_NOT_TODAY: 'الوصول ليس اليوم — حالة النظافة الآن لا تدل على حالتها عند الوصول.',
  LOOKUP_FAILED: 'تعذّر الاطلاع على مهام التنظيف.'
};

export const CLEANING_REASON_UNKNOWN = 'لا تتوفر تفاصيل إضافية عن حالة النظافة.';

/**
 * When the turnover was actually finished, and which ticket it was.
 *
 * Both are LABELLED rather than left as a bare date and a bare code: a turnover
 * completed five weeks ago still grades as READY (the resolver takes the most recent
 * one in a 60-day window), and an unlabelled «٢٨ يونيو» beside «جاهزة» leaves the
 * operator unable to see that — which is the one thing that would let them judge the
 * verdict for themselves.
 */
export const CLEANING_COMPLETED_LABEL = 'اكتمل';
export const CLEANING_TICKET_LABEL = 'التذكرة';

/**
 * «حالة التشغيل» — CONTEXT, NEVER A VERDICT (D104). Since tasks/unit-operational-status.md S4 this is
 * the DERIVED unit status (same resolver as the unit page), rendered with the shared
 * config/unitStatusLabels.js vocabulary. It describes the unit NOW; the verdict above describes the
 * enquired dates — so the hint keeps the two apart.
 */
export const OPERATIONAL_STATUS_LABEL = 'حالة التشغيل';
export const OPERATIONAL_STATUS_HINT = 'حالة الوحدة الآن — قد تختلف عن تواريخ الاستفسار أعلاه.';

// ---- Entry (D103) ----

/**
 * The check-in time, stated. `{time}` is filled with the Arabic-formatted wall clock.
 *
 * «من» and not «الساعة»: the guest is being told the earliest the door opens, which
 * is the honest reading of a code window that starts then.
 */
export const ENTRY_FROM_AR = (time) => `الدخول من ${time}`;

export const ENTRY_SECTION_LABEL = 'الدخول';

/** The property's own recorded policy, displayed and never interpreted. */
export const EARLY_CHECKIN_LABEL = 'سياسة الدخول المبكر';

/**
 * The resolver returns null when the guidebook field is empty — on purpose, so that
 * wording the absence is the panel's job and not the resolver's. Most units have
 * nothing recorded today.
 *
 * NOT «لا يوجد دخول مبكر»: an unfilled field is not a policy of refusal, and an
 * operator who reads it as one will decline something the owner allows.
 */
export const NO_EARLY_CHECKIN_POLICY_AR = 'لا توجد سياسة مسجلة.';

// ---- Observed state (D102) ----

export const STAY_SECTION_LABEL = 'الفترة';
export const AVAILABILITY_SECTION_LABEL = 'التوفر';
export const CLEANING_SECTION_LABEL = 'النظافة';

/**
 * THE DISPUTE RECORD, in words. Between this reading and the guest booking, someone
 * else may reserve the unit, maintenance may close it, or an OTA may sync — so the
 * panel says plainly that it is reporting a moment, not holding a room.
 */
export const NOT_A_HOLD_AR =
  'هذه حالة النظام وقت الاطلاع، وليست حجزاً مؤقتاً للوحدة.';

/** Screen-reader wording for the observed-at stamp, which renders as relative time. */
export const OBSERVED_AT_LABEL = 'وقت الاطلاع';

/**
 * How many nights the availability answer covers.
 *
 * Singular/plural matched to `utils/errorMessages.js`, which is the one place in the
 * app that already inflects this noun — the same MIN_STAY/MAX_STAY answers this
 * panel can surface, so the two would otherwise disagree about the same number.
 * Digits come from `formatCount` at the render site (Arabic-Indic, per the locale
 * standard), so this takes a pre-formatted string, not a raw number.
 */
export const NIGHTS_AR = (formattedCount, nights) =>
  `${formattedCount} ${nights === 1 ? 'ليلة' : 'ليالي'}`;
